import React from 'react';
import { Button } from '@/components/ui/button';
import { Heart, Users, Gift } from 'lucide-react';
import CharityCard from '@/components/charity/CharityCard';
import CharityForm from '@/components/charity/CharityForm';
import { Charity } from '@/types/charity';

interface CharityTabProps {
  charity: Charity | null;
  userId: string;
  isEditingCharity: boolean;
  onEditCharity: () => void;
  onCharitySaved: (charity: Charity) => void;
  onCancelEditCharity: () => void;
}

const CharityTab: React.FC<CharityTabProps> = ({
  charity,
  userId,
  isEditingCharity,
  onEditCharity,
  onCharitySaved,
  onCancelEditCharity
}) => {
  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Heart className="h-6 w-6 text-red-500" />
          Supporting Charity
        </h2>
        <p className="text-muted-foreground text-lg max-w-3xl">
          Make your holiday display meaningful by supporting a cause you care about. When you add a charity, 
          visitors who download your sequences will be encouraged to make donations, spreading joy and making a difference.
        </p>
      </div>
      
      {isEditingCharity ? (
        <div className="max-w-2xl">
          <CharityForm 
            charity={charity} 
            userId={userId} 
            onSaved={onCharitySaved} 
          />
          <Button 
            variant="outline" 
            className="mt-4" 
            onClick={onCancelEditCharity}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          {charity ? (
            <div className="max-w-md mb-6">
              <CharityCard charity={charity} />
              <Button 
                variant="outline" 
                className="mt-4" 
                onClick={onEditCharity}
              >
                Edit Charity Information
              </Button>
            </div>
          ) : (
            <div className="max-w-2xl">
              {/* Hero Section */}
              <div className="bg-linear-to-br from-red-50 to-green-50 p-8 rounded-xl border border-red-100 mb-6">
                <div className="text-center mb-6">
                  <div className="flex justify-center space-x-4 mb-4">
                    <div className="bg-red-100 p-3 rounded-full">
                      <Heart className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Gift className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Turn Your Display Into a Force for Good</h3>
                  <p className="text-gray-700 mb-6">
                    Connect your holiday display to a cause that matters to you. Every sequence download becomes 
                    an opportunity to raise awareness and encourage donations for your chosen charity.
                  </p>
                </div>
                
                {/* Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">🎄 Spread Holiday Spirit</h4>
                    <p className="text-sm text-gray-600">
                      Share the joy of giving during the holiday season by connecting your display to a meaningful cause.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">🌟 Increase Engagement</h4>
                    <p className="text-sm text-gray-600">
                      Visitors are more likely to download and share sequences that support charitable causes.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">❤️ Make a Difference</h4>
                    <p className="text-sm text-gray-600">
                      Turn every download into a potential donation and help organizations that need support.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">🤝 Build Community</h4>
                    <p className="text-sm text-gray-600">
                      Connect with others who share your values and passion for giving back.
                    </p>
                  </div>
                </div>
                
                <div className="text-center">
                  <Button 
                    onClick={onEditCharity}
                    size="lg"
                    className="bg-linear-to-r from-red-600 to-green-600 hover:from-red-700 hover:to-green-700 text-white font-medium px-8 py-3"
                  >
                    <Heart className="mr-2 h-5 w-5" />
                    Add Your Charity
                  </Button>
                  <p className="text-sm text-gray-600 mt-3">
                    It only takes a minute to set up and makes your display more meaningful
                  </p>
                </div>
              </div>
              
              {/* How it Works */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-4">How It Works</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium mt-0.5">1</span>
                    <span>Add your preferred charity with a donation link and description</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium mt-0.5">2</span>
                    <span>When people download your sequences, they'll see information about your charity</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium mt-0.5">3</span>
                    <span>Visitors can choose to make a donation and help support your cause</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CharityTab;
